import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { API_ENDPOINTS } from "../config/apiConfig";
import { TURNSTILE_SITE_KEY } from "../config/env";
import { logger } from "../utils/logger";
import TurnstileWidget from "../components/website/TurnstileWidget";

const initialState = { name: "", email: "", message: "", website: "" };

function contactErrorMessage(error) {
  const apiError = error.response?.data?.error;
  if (typeof apiError === "string" && apiError.trim()) return apiError;
  if (error.message && !error.response) return error.message;
  if (!error.response) return "Could not reach the server. Please try again.";
  return `Something went wrong (${error.response.status}). Please try again.`;
}

function Contact() {
  const location = useLocation();
  const [messageSent, setMessageSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const turnstileRef = useRef(null);
  const [{ name, email, message, website }, setState] = useState(initialState);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname]);

  const handleChange = (e) => {
    const { name: fieldName, value } = e.target;
    setState((prev) => ({ ...prev, [fieldName]: value }));
  };

  const clearState = () => {
    setState({ ...initialState });
    setTurnstileToken("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    setSubmitting(true);
    try {
      let token = turnstileToken;
      if (TURNSTILE_SITE_KEY && turnstileRef.current?.getFreshToken) {
        token = await turnstileRef.current.getFreshToken();
      }
      if (TURNSTILE_SITE_KEY && !token) {
        setErrorMessage("Please complete the verification check.");
        return;
      }

      const response = await axios.post(API_ENDPOINTS.contact, {
        name,
        email,
        message,
        website,
        "cf-turnstile-response": token,
      });
      logger.log(response);
      clearState();
      setMessageSent(true);
    } catch (error) {
      logger.log(error);
      setErrorMessage(contactErrorMessage(error));
      setTurnstileResetKey((value) => value + 1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <section className="relative pt-36 pb-24 overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/40 to-teal-50/30">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/60 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent via-white/60 to-white" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 border border-blue-200 mb-6">
            <span className="text-sm text-blue-700 font-medium">Contact Us</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
            Let&apos;s talk about your
            <br />
            <span className="text-blue-700">EV planning goals</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Have a question or want to book a demo? Send us a message and our team will get back to you soon.
          </p>
        </div>
      </section>

      <section id="contact-form" className="scroll-mt-24 bg-white py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="mb-6 text-center">
            <p className="text-base text-slate-600">
              Prefer email? Send us a note at{" "}
              <a href="mailto:info@amptrans.net" className="font-medium text-blue-700 hover:text-blue-800">
                info@amptrans.net
              </a>
              .
            </p>
          </div>
          <div className="bg-white rounded-2xl p-8 lg:p-10 border border-slate-200 shadow-sm">
            {messageSent ? (
              <div className="text-center py-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-3">Message Sent</h2>
                <p className="text-slate-600">Your message has been sent. We will get back to you as soon as possible.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="relative space-y-6">
                <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={website}
                    onChange={handleChange}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2" htmlFor="name">Name</label>
                    <input id="name" name="name" type="text" value={name} onChange={handleChange} className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2" htmlFor="email">Email</label>
                    <input id="email" name="email" type="email" value={email} onChange={handleChange} className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2" htmlFor="message">Message</label>
                  <textarea id="message" name="message" value={message} onChange={handleChange} rows={6} className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
                {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center bg-blue-700 hover:bg-blue-800 text-white px-8 py-3 rounded-lg font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? "Sending..." : "Send Message"}
                </button>
                <TurnstileWidget
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  action="contact"
                  resetKey={turnstileResetKey}
                  onTokenChange={setTurnstileToken}
                />
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;
